import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';

const ParticleSystem = ({ audioData, vibeConfig }) => {
  const starsRef = useRef();
  const energyRef = useRef();
  
  const { geometry } = vibeConfig || {};
  const starCount = geometry?.particleCount || 300;
  const energyCount = 100;

  // Create star field
  const stars = useMemo(() => {
    const positions = [];
    const starColors = [];
    
    for (let i = 0; i < starCount; i++) {
      // Distribute in a large sphere
      const radius = 20 + Math.random() * 30;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      
      positions.push(
        radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.sin(phi) * Math.sin(theta),
        radius * Math.cos(phi)
      );
      
      // Twinkling star colors
      const brightness = 0.5 + Math.random() * 0.5;
      starColors.push(brightness, brightness, brightness);
    }
    
    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(starColors)
    };
  }, [starCount]);

  // Create energy particles
  const energy = useMemo(() => {
    const positions = [];
    const energyColors = [];
    
    for (let i = 0; i < energyCount; i++) {
      positions.push(
        (Math.random() - 0.5) * 15,
        (Math.random() - 0.5) * 15,
        (Math.random() - 0.5) * 15
      );
      
      // EDM colors - more vibrant and spectrum-responsive
      const colorType = Math.floor(Math.random() * 5);
      if (colorType === 0) {
        energyColors.push(0, 1, 1); // Cyan
      } else if (colorType === 1) {
        energyColors.push(1, 0, 0.5); // Magenta
      } else if (colorType === 2) {
        energyColors.push(0.5, 0, 1); // Purple
      } else if (colorType === 3) {
        energyColors.push(1, 1, 0); // Yellow
      } else {
        energyColors.push(0, 1, 0.5); // Green
      }
    }
    
    return {
      positions: new Float32Array(positions),
      colors: new Float32Array(energyColors)
    };
  }, [energyCount]);

  useFrame((state) => {
    if (!audioData) return;

    const time = state.clock.getElapsedTime();
    
    // Animate stars - subtle twinkling
    if (starsRef.current) {
      const starColors = starsRef.current.geometry.attributes.color.array;
      
      for (let i = 0; i < starCount; i++) {
        const i3 = i * 3;
        const twinkle = 0.3 + Math.sin(time * 2 + i * 0.1) * 0.2 + audioData.average * 0.5;
        starColors[i3] = twinkle;
        starColors[i3 + 1] = twinkle;
        starColors[i3 + 2] = twinkle;
      }
      
      starsRef.current.geometry.attributes.color.needsUpdate = true;
    }
    
    // Animate energy particles - highly responsive
    if (energyRef.current) {
      const positions = energyRef.current.geometry.attributes.position.array;
      const energyColors = energyRef.current.geometry.attributes.color.array;
      
      for (let i = 0; i < energyCount; i++) {
        const i3 = i * 3;
        const freqIndex = Math.floor((i / energyCount) * audioData.frequency.length);
        const amplitude = audioData.frequency[freqIndex] || 0;
        
        // Smooth, flowing movement
        const trebleMultiplier = 1 + audioData.treble * 5;
        positions[i3] += Math.sin(time * 1.5 + i * 0.1) * amplitude * 0.1 * trebleMultiplier;
        positions[i3 + 1] += Math.cos(time * 1.2 + i * 0.15) * amplitude * 0.12 * trebleMultiplier;
        positions[i3 + 2] += Math.sin(time * 0.8 + i * 0.08) * amplitude * 0.08 * trebleMultiplier;
        
        // Intense color pulsing with spectrum colors
        const intensity = 0.5 + amplitude * 3 + audioData.treble * 5;
        const freqRatio = freqIndex / audioData.frequency.length;
        
        // Map frequency to color (like a spectrum analyzer)
        let hue = freqRatio * 360; // Full rainbow spectrum
        const saturation = 0.8 + amplitude * 0.2;
        const lightness = 0.4 + intensity * 0.4;
        
        // Convert HSL to RGB
        const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
        const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
        const m = lightness - c / 2;
        
        let r, g, b;
        if (hue < 60) { r = c; g = x; b = 0; }
        else if (hue < 120) { r = x; g = c; b = 0; }
        else if (hue < 180) { r = 0; g = c; b = x; }
        else if (hue < 240) { r = 0; g = x; b = c; }
        else if (hue < 300) { r = x; g = 0; b = c; }
        else { r = c; g = 0; b = x; }
        
        energyColors[i3] = Math.min(1, r + m);
        energyColors[i3 + 1] = Math.min(1, g + m);
        energyColors[i3 + 2] = Math.min(1, b + m);
      }
      
      energyRef.current.geometry.attributes.position.needsUpdate = true;
      energyRef.current.geometry.attributes.color.needsUpdate = true;
    }
  });

  return (
    <group>
      {/* Background stars */}
      <points ref={starsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={starCount}
            array={stars.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={starCount}
            array={stars.colors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.5}
          vertexColors
          transparent
          opacity={0.8}
          sizeAttenuation={true}
        />
      </points>

      {/* Energy particles */}
      <points ref={energyRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={energyCount}
            array={energy.positions}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            count={energyCount}
            array={energy.colors}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={audioData ? 0.3 + audioData.peak * 0.7 + audioData.treble * 1.0 : 0.3}
          vertexColors
          transparent
          opacity={0.9}
          sizeAttenuation={false}
          blending={2} // Additive blending for energy effect
        />
      </points>
    </group>
  );
};

export default ParticleSystem;